import React, { useEffect, useRef, useState } from 'react';
import { BrainCircuit, FileAudio, FileText, Pause, Play, Sparkles, StopCircle, Trash2, Upload, User, Mic } from 'lucide-react';
import { Patient } from '../types';
import {
  deleteRecording as deleteRecordingApi,
  getRecordingPlaybackUrl,
  updateRecordingPatient,
  uploadRecordingFile,
  type RecordingResponse,
} from '../services/recordingsApi';
import { describeError } from '../utils/describeError';

type Recording = RecordingResponse;

interface RecordingsProps {
  patients: Patient[];
  recordings: Recording[];
  onRecordingsChange: (recs: Recording[]) => void;
  currentPatientId?: string;
  onTriggerAIAnalysis?: (recordingId: string) => void;
  onTriggerTranscription?: (recordingId: string) => void;
  /** Supervisão clínica completa da sessão (distinta da Análise IA). */
  onTriggerSupervision?: (recordingId: string) => void;
  /**
   * Quando embutido no prontuário de um paciente, o vínculo já está definido:
   * o seletor de paciente some e tudo que for gravado/enviado fica atrelado a
   * este id. Sem isso, dava para analisar a sessão de outro paciente estando
   * dentro do prontuário.
   */
  lockedPatientId?: string;
}

const Recordings: React.FC<RecordingsProps> = ({
  patients,
  recordings,
  onRecordingsChange,
  currentPatientId,
  onTriggerAIAnalysis,
  onTriggerTranscription,
  onTriggerSupervision,
  lockedPatientId,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedPatientId, setSelectedPatientId] = useState(() => lockedPatientId || currentPatientId || '');
  const [isPlaying, setIsPlaying] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    const forced = lockedPatientId || currentPatientId;
    if (forced) setSelectedPatientId(forced);
  }, [currentPatientId, lockedPatientId]);

  /** No prontuário o vínculo é fixo; fora dele, o usuário escolhe. */
  const patientLinkEditable = !lockedPatientId;

  /**
   * Dentro do prontuário só aparecem as gravações daquele paciente — antes a
   * lista trazia as sessões de todos, o que permitia analisar o áudio errado.
   */
  const visibleRecordings = lockedPatientId
    ? recordings.filter((rec) => rec.patientId === lockedPatientId)
    : recordings;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDisplayDate = (date: string) => {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('pt-BR');
  };

  const handleLinkPatient = async (recordingId: string, patientId: string) => {
    setRecordingError(null);
    try {
      const updated = await updateRecordingPatient(recordingId, patientId || undefined);
      onRecordingsChange(recordings.map((rec) => (rec.id === recordingId ? updated : rec)));
    } catch (error) {
      console.error(error);
      setRecordingError(`Não foi possível atualizar o vínculo do paciente: ${describeError(error)}`);
    }
  };

  const startRecording = async () => {
    setRecordingError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      // Bloco 49: 64 kbps mantém a fala nítida e 1 h de sessão em ~29 MB.
      const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64_000 });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        try {
          setIsUploading(true);
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          if (audioBlob.size <= 0) {
            setRecordingError('Nenhum áudio foi capturado. Tente novamente.');
            return;
          }
          const durationSeconds = recordingStartedAtRef.current
            ? Math.max(1, Math.round((Date.now() - recordingStartedAtRef.current) / 1000))
            : Math.max(1, recordingTime);
          const patient = patients.find((p) => p.id === selectedPatientId);
          const newRecording = await uploadRecordingFile({
            file: audioBlob,
            fileName: `sessao-presencial-${new Date().toISOString().slice(0, 10)}.webm`,
            contentType: audioBlob.type || mimeType,
            patientId: selectedPatientId || undefined,
            durationSeconds,
          });
          onRecordingsChange([{ ...newRecording, patientName: patient?.name || newRecording.patientName }, ...recordings]);
        } catch (error) {
          console.error(error);
          setRecordingError(`Erro ao enviar gravação de áudio: ${describeError(error)}`);
        } finally {
          setIsUploading(false);
          setRecordingTime(0);
          recordingStartedAtRef.current = null;
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      timerRef.current = window.setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
    } catch (err) {
      console.error(err);
      setRecordingError(`Erro ao acessar microfone: ${describeError(err)}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRecordingError(null);
    const isAudio = file.type.startsWith('audio') || /\.(mp3|wav|m4a|ogg|webm)$/i.test(file.name);
    if (!isAudio) {
      setRecordingError('Envie um arquivo de áudio válido nos formatos WEBM, MP3, M4A, OGG ou WAV.');
      e.target.value = '';
      return;
    }

    const patient = patients.find((p) => p.id === selectedPatientId);
    try {
      setIsUploading(true);
      const newRecording = await uploadRecordingFile({
        file,
        fileName: file.name,
        contentType: file.type || 'audio/mpeg',
        patientId: selectedPatientId || undefined,
      });
      onRecordingsChange([{ ...newRecording, patientName: patient?.name || newRecording.patientName }, ...recordings]);
    } catch (error) {
      console.error(error);
      setRecordingError(`Erro ao enviar áudio: ${describeError(error)}`);
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const deleteRecording = async (id: string) => {
    setRecordingError(null);
    try {
      await deleteRecordingApi(id);
      onRecordingsChange(recordings.filter((rec) => rec.id !== id));
    } catch (error) {
      console.error(error);
      setRecordingError(`Não foi possível excluir a gravação: ${describeError(error)}`);
    }
  };

  const togglePlayback = async (rec: Recording) => {
    if (isPlaying === rec.id) {
      audioPlayerRef.current?.pause();
      setIsPlaying(null);
      return;
    }

    try {
      const playback = rec.url ? { url: rec.url } : await getRecordingPlaybackUrl(rec.id);
      if (!audioPlayerRef.current) return;
      audioPlayerRef.current.src = playback.url;
      await audioPlayerRef.current.play();
      setIsPlaying(rec.id);
    } catch (error) {
      console.error(error);
      setRecordingError(`Não foi possível gerar o link temporário do áudio: ${describeError(error)}`);
    }
  };

  const canPlay = (rec: Recording) => rec.type === 'audio' && rec.status === 'READY';

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <audio ref={audioPlayerRef} onEnded={() => setIsPlaying(null)} className="hidden" />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black dark:text-white tracking-tight">Gravações</h1>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mt-1">Gerencie sessões presenciais e arquivos externos.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
          {patientLinkEditable && (
          <div className="relative group">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={16} />
            <select
              value={selectedPatientId}
              onChange={(e) => setSelectedPatientId(e.target.value)}
              className="w-full md:w-auto pl-11 pr-10 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 md:min-w-[260px] shadow-sm appearance-none cursor-pointer"
            >
              <option value="">Vincular Paciente...</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          )}

          <div className="flex items-center gap-4">
            {isRecording ? (
              <button onClick={stopRecording} className="bg-red-500 text-white px-5 sm:px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-3 shadow-xl active:scale-95 animate-pulse h-[46px]"><StopCircle size={20} /> {formatTime(recordingTime)}</button>
            ) : (
              <button onClick={startRecording} disabled={isUploading} className="bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white px-5 sm:px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-3 shadow-lg active:scale-95 transition-all h-[46px]"><Mic size={20} /> Iniciar Gravação</button>
            )}

            <div className="relative">
              <label className={`bg-emerald-500 hover:bg-emerald-600 text-white px-5 sm:px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-3 shadow-lg transition-all h-[46px] ${isUploading ? 'opacity-60 cursor-wait' : 'cursor-pointer active:scale-95'}`}>
                <Upload size={20} /> {isUploading ? 'Enviando...' : 'Upload de Áudio'}
                <input type="file" className="hidden" onChange={handleFileUpload} accept=".mp3,.m4a,.webm,.ogg,.wav,audio/*" disabled={isUploading} />
              </label>
              <div className="absolute top-full right-2 mt-1 whitespace-nowrap text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 opacity-70">
                Formatos: WEBM, MP3, M4A, WAV
              </div>
            </div>
          </div>
        </div>
      </div>

      {recordingError && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-100 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-[11px] font-bold text-rose-600 dark:text-rose-300">
          <FileText size={16} className="shrink-0 mt-0.5" />
          <span>{recordingError}</span>
        </div>
      )}

      <div className="md:hidden space-y-3">
        {visibleRecordings.map((rec) => (
          <div key={rec.id} className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-3 rounded-2xl shadow-sm bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400">
                  <FileAudio size={18} />
                </div>
                <div className="min-w-0">
                  <span className="font-black text-sm block dark:text-slate-100 tracking-tight truncate">{rec.name}</span>
                  <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1 block">{formatDisplayDate(rec.date)}</span>
                </div>
              </div>
              <span className="text-[10px] font-black bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 shrink-0">{rec.duration}</span>
            </div>
            {patientLinkEditable && (
              <select
                value={rec.patientId || ''}
                onChange={(e) => handleLinkPatient(rec.id, e.target.value)}
                className={`w-full bg-transparent border py-2 px-3 rounded-xl text-[11px] font-black uppercase tracking-widest focus:outline-none transition-all cursor-pointer ${rec.patientId ? 'border-indigo-100 dark:border-indigo-900 text-slate-700 dark:text-slate-200' : 'border-amber-200 dark:border-amber-900 text-amber-600'}`}
              >
                <option value="">Vincular agora...</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            {rec.type === 'audio' && (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => onTriggerAIAnalysis?.(rec.id)}
                  className="flex-1 px-5 py-2.5 bg-indigo-600 text-white hover:bg-indigo-700 transition-all rounded-2xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md active:scale-95 whitespace-nowrap"
                  title="Submeter para Análise de IA Avançada"
                >
                  <Sparkles size={12} className="text-white shrink-0" />
                  <span>Análise IA</span>
                </button>
                <button
                  onClick={() => onTriggerTranscription?.(rec.id)}
                  className="flex-1 px-5 py-2.5 bg-[#800020] text-white hover:bg-[#6b001b] transition-all rounded-2xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md active:scale-95 whitespace-nowrap"
                  title="Submeter para Transcrição / Ver Transcrição"
                >
                  <FileText size={12} className="text-white shrink-0" />
                  <span>Transcrição</span>
                </button>
              </div>
            )}
            <div className="flex justify-end gap-3">
              {canPlay(rec) && (
                <button
                  onClick={() => togglePlayback(rec)}
                  className={`p-3 rounded-2xl transition-all ${isPlaying === rec.id ? 'bg-indigo-600 text-white scale-105' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white'}`}
                  title={isPlaying === rec.id ? 'Pausar' : 'Ouvir Gravação'}
                >
                  {isPlaying === rec.id ? <Pause size={18} /> : <Play size={18} />}
                </button>
              )}
              <button onClick={() => deleteRecording(rec.id)} className="p-3 bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-500 hover:text-white rounded-2xl transition-all"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
        <table className="w-full min-w-[980px] text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-[0.2em]">
              <th className="px-5 sm:px-10 py-5">Arquivo / Sessão</th>
              {patientLinkEditable && <th className="px-5 sm:px-10 py-5 text-center">Vínculo de Paciente</th>}
              <th className="px-5 sm:px-10 py-5 text-center">Processamento Clínico</th>
              <th className="px-5 sm:px-10 py-5 text-center">Duração</th>
              <th className="px-5 sm:px-10 py-5 text-right">Ações Clínicas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {visibleRecordings.map((rec) => (
              <tr key={rec.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors group">
                <td className="px-5 sm:px-10 py-6">
                  <div className="flex items-center gap-5">
                    <div className="p-3 rounded-2xl shadow-sm bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400">
                      <FileAudio size={20} />
                    </div>
                    <div>
                      <span className="font-black text-sm block dark:text-slate-100 tracking-tight">{rec.name}</span>
                      <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1 block">{formatDisplayDate(rec.date)}</span>
                    </div>
                  </div>
                </td>
                {patientLinkEditable && (
                  <td className="px-5 sm:px-10 py-6 text-center">
                    <select
                      value={rec.patientId || ''}
                      onChange={(e) => handleLinkPatient(rec.id, e.target.value)}
                      className={`bg-transparent border-b-2 py-1 text-[11px] font-black uppercase tracking-widest focus:outline-none transition-all cursor-pointer ${rec.patientId ? 'border-indigo-100 dark:border-indigo-900 text-slate-700 dark:text-slate-200' : 'border-amber-100 dark:border-amber-900 text-amber-600'}`}
                    >
                      <option value="">Vincular agora...</option>
                      {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                )}
                <td className="px-5 sm:px-10 py-6">
                  {rec.type === 'audio' ? (
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => onTriggerAIAnalysis?.(rec.id)}
                        className="px-5 py-2.5 bg-indigo-600 text-white hover:bg-indigo-700 transition-all rounded-2xl text-[10px] font-black uppercase tracking-wider flex items-center gap-2 shadow-md hover:scale-105 active:scale-95 border-none whitespace-nowrap"
                        title="Submeter para Análise de IA Avançada"
                      >
                        <Sparkles size={12} className="text-white shrink-0" />
                        <span>Análise IA</span>
                      </button>
                      <button
                        onClick={() => onTriggerTranscription?.(rec.id)}
                        className="px-5 py-2.5 bg-[#800020] text-white hover:bg-[#6b001b] transition-all rounded-2xl text-[10px] font-black uppercase tracking-wider flex items-center gap-2 shadow-md hover:scale-105 active:scale-95 border-none whitespace-nowrap"
                        title="Submeter para Transcrição / Ver Transcrição"
                      >
                        <FileText size={12} className="text-white shrink-0" />
                        <span>Transcrição</span>
                      </button>
                      {onTriggerSupervision && (
                        <button
                          onClick={() => onTriggerSupervision(rec.id)}
                          className="px-5 py-2.5 bg-emerald-700 text-white hover:bg-emerald-800 transition-all rounded-2xl text-[10px] font-black uppercase tracking-wider flex items-center gap-2 shadow-md hover:scale-105 active:scale-95 border-none whitespace-nowrap"
                          title="Gerar supervisão clínica completa desta sessão"
                        >
                          <BrainCircuit size={12} className="text-white shrink-0" />
                          <span>Supervisionar</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-[10px] text-slate-400 uppercase font-black tracking-widest">Sem áudio</div>
                  )}
                </td>
                <td className="px-5 sm:px-10 py-6 text-center">
                  <span className="text-[10px] font-black bg-slate-100 dark:bg-slate-800 px-4 py-1.5 rounded-full text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">{rec.duration}</span>
                </td>
                <td className="px-5 sm:px-10 py-6 text-right">
                  <div className="flex justify-end gap-3">
                    {canPlay(rec) && (
                      <button
                        onClick={() => togglePlayback(rec)}
                        className={`p-3 rounded-2xl transition-all ${isPlaying === rec.id ? 'bg-indigo-600 text-white scale-105' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white'}`}
                        title={isPlaying === rec.id ? 'Pausar' : 'Ouvir Gravação'}
                      >
                        {isPlaying === rec.id ? <Pause size={18} /> : <Play size={18} />}
                      </button>
                    )}
                    <button onClick={() => deleteRecording(rec.id)} className="p-3 bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-500 hover:text-white rounded-2xl transition-all"><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visibleRecordings.length === 0 && (
          <div className="p-32 flex flex-col items-center justify-center text-slate-300 dark:text-slate-700 text-center">
            <FileAudio size={64} className="opacity-10 mb-6" />
            <p className="text-lg font-black uppercase tracking-widest tracking-tighter">Nenhum áudio clínico capturado</p>
            <p className="text-[10px] uppercase font-black tracking-widest mt-3 text-slate-400">Grave áudio ou faça upload de arquivos WEBM/MP3/WAV</p>
          </div>
        )}
      </div>
      {visibleRecordings.length === 0 && (
        <div className="md:hidden p-6 sm:p-12 flex flex-col items-center justify-center text-slate-300 dark:text-slate-700 text-center bg-white dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800">
          <FileAudio size={48} className="opacity-10 mb-5" />
          <p className="text-sm font-black uppercase tracking-widest tracking-tighter">Nenhum áudio clínico capturado</p>
        </div>
      )}
    </div>
  );
};

export default Recordings;
